/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Pulse › Digests (strategy 9.7; contract 9.2): the daily and weekly digests from GET /v1/admin/pulse/digests, newest
 * first, each section collapsible and every claim linked to its records, feedback entries and problems; Mark read; Run
 * now for an admin who wants today's digest early (one an hour); Copy "for the dev team". `?digest=` opens one.
 */
import { useEffect, useState } from 'react';
import { Check, Copy, Newspaper, Play } from 'lucide-react';
import { api } from '../../lib/api';
import {
  domainLabel, forDevTeamText, healthChip, pctText, pulseExportHref, pulseHref, pulseReadFailure, reasonLabel, subjectName, usdText, whenText,
  type PulseDigest, type PulseDigestListItem,
} from '../../lib/pulse';
import { SaveNote } from '../../components/SaveNote';
import { Button, EmptyState, PageHeader, Panel, Select, Skeleton, Stack, StatusChip } from '../../ui';
import { count } from '../../ui/format';
import { Chip, ExportButton, PageLink, PulseError, usePagedList, useQueryParam } from './common';

export function PulseDigests({ canEdit }: { canEdit: boolean }) {
  const [kind, setKind] = useState<'daily' | 'weekly'>('daily');
  const [running, setRunning] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [openId, setOpenId] = useQueryParam('digest');
  const list = usePagedList((cursor) => api.pulseDigests({ sort: '-createdAt', cursor, limit: 20 }), 'digests');

  const run = async () => {
    setRunning(true);
    setNote(null);
    try {
      const result = await api.runPulseDigest(kind);
      list.setItems((items) => items.filter((item) => item.id !== result.digest.id));
      list.prepend(result.digest);
      setOpenId(result.digest.id);
      setNote({ ok: true, text: `The ${kind} digest was written just now by ${result.digest.model}.` });
    } catch (error) {
      setNote({ ok: false, text: pulseReadFailure(error, 'The digest could not be run.') });
    } finally {
      setRunning(false);
    }
  };

  const unread = list.items.filter((item) => !item.readAt).length;

  return (
    <>
      <PageHeader
        meta={<span>The analyst's daily and weekly read of Pulse, newest first.{list.load.state === 'ready' ? ` ${count(list.total)} digests, ${count(unread)} unread on this page.` : ''}</span>}
        actions={(
          <>
            <ExportButton href={pulseExportHref('digests')} />
            {canEdit ? (
              <>
                <Select size="sm" className="ui-w-auto" aria-label="Digest to run" value={kind} onChange={(event) => setKind(event.target.value as 'daily' | 'weekly')}>
                  <option value="daily">Today's digest</option>
                  <option value="weekly">This week's digest</option>
                </Select>
                <Button size="sm" variant="primary" icon={Play} busy={running} onClick={() => void run()} title="Write the digest now instead of waiting for its time (one run an hour)">Run now</Button>
              </>
            ) : null}
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          <SaveNote note={note} />
          {list.load.state === 'loading' ? <Panel title="Loading digests"><div aria-busy="true"><Skeleton size="lg" /><Skeleton /><Skeleton /></div></Panel> : null}
          {list.load.state === 'failed' ? <PulseError title="The digests could not be loaded" error={list.load.error} onRetry={list.reload} retrying={list.reloading} /> : null}
          {list.load.state === 'ready' && list.items.length === 0 ? (
            <EmptyState icon={Newspaper} title="No digest yet">The daily digest is written at the time set under Pulse › Settings, the weekly on its day.{canEdit ? ' Run now writes one early.' : ''}</EmptyState>
          ) : null}
          {list.items.map((item, index) => (
            <DigestCard
              key={item.id}
              item={item}
              open={openId ? openId === item.id : index === 0}
              canEdit={canEdit}
              onToggle={(next) => setOpenId(next ? item.id : null)}
              onChanged={(digest) => list.setItems((items) => items.map((row) => (row.id === digest.id ? digest : row)))}
              onNote={setNote}
            />
          ))}
          {list.next ? <div className="ui-actions"><Button busy={list.moreLoading} onClick={() => list.loadMore(list.next as string)}>Load more</Button></div> : null}
        </Stack>
      </div>
    </>
  );
}

const SECTION_HELP = {
  asked: 'What people asked',
  notWorking: 'What is not working',
  notPossible: 'Asked for but not possible',
  health: 'Health changes',
  feedbackWorthReading: 'Feedback worth reading',
  suggestedChanges: 'Suggested changes',
  pulseQuality: 'Pulse quality',
} as const;

function RecordLinks({ ids }: { ids: string[] }) {
  if (!ids.length) return null;
  return <span className="pulse-links">{ids.slice(0, 5).map((id) => <PageLink key={id} href={pulseHref.record(id)}>record</PageLink>)}{ids.length > 5 ? <span className="ui-quiet">+{ids.length - 5} more</span> : null}</span>;
}

/** One digest: its head, and the sections when it is open (read on first opening when the list sent only the head). */
function DigestCard({ item, open, canEdit, onToggle, onChanged, onNote }: { item: PulseDigestListItem; open: boolean; canEdit: boolean; onToggle: (open: boolean) => void; onChanged: (digest: PulseDigest) => void; onNote: (note: { ok: boolean; text: string }) => void }) {
  const [detail, setDetail] = useState<PulseDigest | null>(item.asked ? (item as PulseDigest) : null);
  const [detailError, setDetailError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!open || detail || detailError) return;
    let live = true;
    api.pulseDigest(item.id).then((digest) => { if (live) setDetail(digest); }, (error: unknown) => { if (live) setDetailError(error); });
    return () => { live = false; };
  }, [open, detail, detailError, item.id]);
  useEffect(() => {
    if (item.asked) setDetail(item as PulseDigest);
  }, [item]);

  const markRead = async () => {
    setBusy(true);
    try {
      const result = await api.markPulseDigestRead(item.id);
      onChanged(result.digest ?? { ...(detail ?? (item as PulseDigest)), readAt: Date.now() });
      onNote({ ok: true, text: 'Marked read.' });
    } catch (error) {
      onNote({ ok: false, text: pulseReadFailure(error, 'The digest could not be marked read.') });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!detail) return;
    try {
      await navigator.clipboard.writeText(forDevTeamText(detail));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      onNote({ ok: false, text: 'The text could not be copied; select it in the digest instead.' });
    }
  };

  const title = `${item.kind === 'weekly' ? 'Weekly' : 'Daily'} digest · ${item.from}${item.to !== item.from ? ` to ${item.to}` : ''}`;
  return (
    <Panel
      title={title}
      description={`written ${whenText(item.createdAt)} by ${item.model}`}
      actions={(
        <span className="pulse-actions">
          {item.readAt ? <StatusChip tone="neutral" plain>read {whenText(item.readAt)}</StatusChip> : <StatusChip tone="accent">unread</StatusChip>}
          <Button size="sm" variant="ghost" onClick={() => onToggle(!open)} aria-expanded={open}>{open ? 'Collapse' : 'Open'}</Button>
          {open && detail ? <Button size="sm" icon={copied ? Check : Copy} onClick={() => void copy()}>{copied ? 'Copied' : 'Copy "for the dev team"'}</Button> : null}
          {canEdit && !item.readAt ? <Button size="sm" variant="primary" icon={Check} busy={busy} onClick={() => void markRead()}>Mark read</Button> : null}
        </span>
      )}
    >
      {!open ? null : detailError ? <PulseError title="The digest could not be read" error={detailError} onRetry={() => setDetailError(null)} /> : !detail ? <div aria-busy="true"><Skeleton size="lg" /><Skeleton /><Skeleton /></div> : (
        <div className="ui-details-list">
          {detail.forDevTeam ? (
            <details className="ui-details" open>
              <summary><strong>For the dev team</strong></summary>
              <div className="ui-details-body"><p className="pulse-analysis">{detail.forDevTeam}</p></div>
            </details>
          ) : null}
          <details className="ui-details" open>
            <summary><strong>{SECTION_HELP.suggestedChanges}</strong> <span className="ui-quiet">{count(detail.suggestedChanges.length)}</span></summary>
            <div className="ui-details-body">
              {detail.suggestedChanges.length ? (
                <ul className="pulse-claims">
                  {detail.suggestedChanges.map((change, index) => (
                    <li key={index}><strong>{change.title}</strong> <StatusChip tone={change.confidence === 'likely' ? 'ok' : change.confidence === 'possible' ? 'warn' : 'neutral'} plain>{change.kind} · {change.confidence}</StatusChip> {change.why} {change.problemId ? <PageLink href={pulseHref.problem(change.problemId)}>{change.problemId}</PageLink> : null} {change.evidence.length ? <RecordLinks ids={change.evidence} /> : null}</li>
                  ))}
                </ul>
              ) : <p className="ui-para ui-quiet">Nothing suggested.</p>}
            </div>
          </details>
          <details className="ui-details" open>
            <summary><strong>{SECTION_HELP.notWorking}</strong> <span className="ui-quiet">{count(detail.notWorking.length)}</span></summary>
            <div className="ui-details-body">
              {detail.notWorking.length ? (
                <ul className="pulse-claims">
                  {detail.notWorking.map((row) => (
                    <li key={row.intentKey}><PageLink href={pulseHref.intent(row.intentKey)}>{row.intentKey.replace(/-/g, ' ')}</PageLink> ({domainLabel(row.domain)}): {count(row.asks)} asks, {pctText(row.couldNotPct)} could not ({count(row.saidCannot)} said it couldn't, {count(row.triedFailed)} tried and failed), {count(row.undo)} undone. {row.samples.map((sample, i) => <q key={i} className="ui-quiet">{sample}</q>)} <RecordLinks ids={row.recordIds} /></li>
                  ))}
                </ul>
              ) : <p className="ui-para ui-quiet">Nothing failing enough to list.</p>}
            </div>
          </details>
          <details className="ui-details">
            <summary><strong>{SECTION_HELP.notPossible}</strong> <span className="ui-quiet">{count(detail.notPossible.length)}</span></summary>
            <div className="ui-details-body">
              {detail.notPossible.length ? (
                <ul className="pulse-claims">
                  {detail.notPossible.map((row) => <li key={row.intentKey}><PageLink href={pulseHref.intent(row.intentKey)}>{row.intentKey.replace(/-/g, ' ')}</PageLink> ({domainLabel(row.domain)}): {count(row.asks)} asks, {reasonLabel(row.reason)}; {count(row.repeats)} repeats, {count(row.rephrases)} rephrases. <RecordLinks ids={row.recordIds} /></li>)}
                </ul>
              ) : <p className="ui-para ui-quiet">Nothing asked for that Gyro could not do.</p>}
            </div>
          </details>
          <details className="ui-details">
            <summary><strong>{SECTION_HELP.health}</strong> <span className="ui-quiet">{count(detail.health.length)}</span></summary>
            <div className="ui-details-body">
              {detail.health.length ? (
                <ul className="pulse-claims">
                  {detail.health.map((row, index) => <li key={index}><PageLink href={pulseHref.subject(row.subjectId)}><code>{subjectName(row.subjectId).name}</code></PageLink> <Chip spec={healthChip(row.from)} /> → <Chip spec={healthChip(row.to)} /> {whenText(row.at)} {row.problemId ? <PageLink href={pulseHref.problem(row.problemId)}>{row.problemId}</PageLink> : null}</li>)}
                </ul>
              ) : <p className="ui-para ui-quiet">No tool changed state.</p>}
            </div>
          </details>
          <details className="ui-details">
            <summary><strong>{SECTION_HELP.feedbackWorthReading}</strong> <span className="ui-quiet">{count(detail.feedbackWorthReading.length)}</span></summary>
            <div className="ui-details-body">
              {detail.feedbackWorthReading.length ? (
                <ul className="pulse-claims">
                  {detail.feedbackWorthReading.map((theme, index) => <li key={index}><strong>{theme.theme}</strong> {theme.quotes.map((quote, i) => <q key={i}>{quote}</q>)} <span className="pulse-links">{theme.feedbackKeys.slice(0, 5).map((key) => <PageLink key={key} href={pulseHref.feedback(key)}>entry</PageLink>)}</span></li>)}
                </ul>
              ) : <p className="ui-para ui-quiet">No comments worth a theme.</p>}
            </div>
          </details>
          <details className="ui-details">
            <summary><strong>{SECTION_HELP.asked}</strong> <span className="ui-quiet">{count(detail.asked.length)}</span></summary>
            <div className="ui-details-body">
              {detail.asked.length ? (
                <ul className="pulse-claims">
                  {detail.asked.map((row) => <li key={`${row.domain}-${row.intentKey}`}><PageLink href={pulseHref.intent(row.intentKey)}>{row.intentKey.replace(/-/g, ' ')}</PageLink> ({domainLabel(row.domain)}): {count(row.asks)} asks, helped {pctText(row.helpedPct)}{row.new ? <> <StatusChip tone="info" plain>new</StatusChip></> : null}</li>)}
                </ul>
              ) : <p className="ui-para ui-quiet">Nothing asked.</p>}
            </div>
          </details>
          {detail.topTen?.length ? (
            <details className="ui-details">
              <summary><strong>Top ten</strong> <span className="ui-quiet">count × failure rate × repeat rate</span></summary>
              <div className="ui-details-body"><ol className="pulse-claims">{detail.topTen.map((row) => <li key={row.intentKey}><PageLink href={pulseHref.intent(row.intentKey)}>{row.intentKey.replace(/-/g, ' ')}</PageLink> · {row.score.toFixed(1)} · {row.why}</li>)}</ol></div>
            </details>
          ) : null}
          {detail.trends?.length ? (
            <details className="ui-details">
              <summary><strong>Trends</strong> <span className="ui-quiet">helped rate by week</span></summary>
              <div className="ui-details-body"><ul className="pulse-claims">{detail.trends.map((row) => <li key={row.domain}><PageLink href={pulseHref.domain(row.domain)}>{domainLabel(row.domain)}</PageLink>: {row.helpedPctByWeek.map((pct) => pctText(pct)).join(' → ')}</li>)}</ul></div>
            </details>
          ) : null}
          <details className="ui-details">
            <summary><strong>{SECTION_HELP.pulseQuality}</strong></summary>
            <div className="ui-details-body">
              <p className="ui-para">Outcome unknown {pctText(detail.pulseQuality.unknownPct, 1)} · judge confidence p50 {Math.round(detail.pulseQuality.judgeConfidenceP50 * 100)} % · {count(detail.pulseQuality.cardsShown)} cards shown, {count(detail.pulseQuality.cardsAnswered)} answered · spend {usdText(detail.pulseQuality.spendUsd)} · prompt {detail.promptVersion}</p>
            </div>
          </details>
        </div>
      )}
    </Panel>
  );
}
